import { useChainId } from "lib/chains";
import useSWR from "swr";
import { useWeb3React } from "@web3-react/core";
import { useLiquidityEventContract } from "pages/Event/hooks/blockchain/contracts/useLiquidityEventContract";
import { ApolloQueryResult, gql } from "@apollo/client";
import { useEffect, useState } from "react";
import { getGmxGraphClient } from "../../../../lib/subgraph";

type GlobalStats = {
  currentTier: number;
  destroyedPlp: string;
  id: string;
  paidAmount: string;
  purchasedPlp: string;
  totalBurntRewards: string;
  totalRewardsAllocated: string;
  totalRewardsClaimed: string;
  totalRewardsDestroyed: string;
};

export const useEventGeneralInfoQuery = () => {
  const { chainId } = useChainId();
  const { active } = useWeb3React();
  const eventContract = useLiquidityEventContract();
  const [globalStats, setGlobalStats] = useState<GlobalStats>();

  const fetcher = async () => {
    const [eventEnded, stopped, totalAllocatedPlp, totalPurchased, currentTier, maxTier, plp] = await Promise.all([
      eventContract.eventEnded(),
      eventContract.stopped(),
      eventContract.totalAllocatedPlp(),
      eventContract.totalPurchased(),
      eventContract.currentTier(),
      eventContract.maxTier(),
      eventContract.plp(),
    ]);

    const tiersInfo = await Promise.all(
      Array(maxTier.toNumber() + 1)
        .fill(null)
        .map(async (el, tierIndex) => {
          const [tier, rewardPerToken, upper, lower, allocatedPlp, filled] = await eventContract.tierInfo(tierIndex);
          return {
            tier,
            rewardPerToken,
            upper,
            lower,
            allocatedPlp,
            filled,
          };
        })
    );

    return {
      eventEnded,
      stopped,
      totalAllocatedPlp,
      totalPurchased,
      currentTier,
      maxTier,
      plp,
      tiersInfo,
    };
  };

  const query = gql(`{
    globalLEStat(id: "GlobalLEStat") {
      currentTier
      destroyedPlp
      id
      paidAmount
      purchasedPlp
      totalBurntRewards
      totalRewardsAllocated
      totalRewardsClaimed
      totalRewardsDestroyed
    }
  }`);
  useEffect(() => {
    // eslint-disable-next-line no-console
    const watch = getGmxGraphClient(chainId)
      ?.watchQuery({
        query,
        pollInterval: 60 * 5 * 1000,
        fetchPolicy: "cache-first",
      })
      .subscribe(
        (res: ApolloQueryResult<{ globalLEStat: GlobalStats }>) => {
          setGlobalStats(res.data.globalLEStat);
        },
        // eslint-disable-next-line no-console
        console.warn
      );
    return () => {
      watch?.unsubscribe();
    };
  }, [setGlobalStats, query, chainId]);

  const result = useSWR<Awaited<ReturnType<typeof fetcher>>>(
    [`LiquidityEvent:general-info:${active}`, chainId],
    fetcher,
    { refreshInterval: 10 * 1000 }
  );

  return {
    ...result,
    globalStats,
  };
};
